import { useTranslation } from 'react-i18next';
import { FileText, Paperclip, X } from 'lucide-react';
import { Btn } from '@/presentation/component/ui';
import { ACCEPTED_DOCUMENT_TYPES, documentFileKey } from '@/presentation/util/documentType';
import type { CourseDocument } from '@/domain/model/CourseDocument';

interface DocumentRowProps {
  fileName: string;
  removeLabel: string;
  isRemoveDisabled?: boolean;
  onRemove: () => void;
}

const DocumentRow = ({ fileName, removeLabel, isRemoveDisabled, onRemove }: DocumentRowProps) => (
  <li className="flex items-center gap-2 rounded-lg border border-[var(--color-line)] bg-[var(--color-surface-2)] px-3 py-2">
    <FileText className="h-4 w-4 shrink-0 text-[var(--color-ink-faint)]" />
    <span className="min-w-0 flex-1 truncate text-sm text-[var(--color-ink)]">{fileName}</span>
    <Btn
      size="sm"
      kind="ghost"
      type="button"
      aria-label={removeLabel}
      disabled={isRemoveDisabled}
      onClick={onRemove}
    >
      <X className="h-4 w-4 text-[var(--color-bad)]" />
    </Btn>
  </li>
);

interface CourseDocumentsFieldProps {
  savedDocuments: CourseDocument[];
  selectedFiles: File[];
  error: string | null;
  isRemoveDisabled: boolean;
  documentInputRef: React.RefObject<HTMLInputElement>;
  onFilesChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveSaved: (documentId: string) => void;
  onRemoveSelected: (file: File) => void;
}

export const CourseDocumentsField = ({
  savedDocuments,
  selectedFiles,
  error,
  isRemoveDisabled,
  documentInputRef,
  onFilesChange,
  onRemoveSaved,
  onRemoveSelected,
}: CourseDocumentsFieldProps) => {
  const { t } = useTranslation();
  const hasDocuments = savedDocuments.length > 0 || selectedFiles.length > 0;

  return (
    <div className="flex flex-col gap-1.5">
      <label
        htmlFor="course-documents"
        className="text-sm font-medium text-[var(--color-ink-soft)]"
      >
        {t('admin.courseDocuments')}
      </label>
      <div className="flex items-center gap-3">
        <label
          htmlFor="course-documents"
          className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-[var(--color-line-strong)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-ink)] transition-colors hover:bg-[var(--color-surface-2)]"
        >
          <Paperclip className="h-4 w-4" />
          {t('admin.addDocuments')}
        </label>
        <input
          ref={documentInputRef}
          id="course-documents"
          type="file"
          multiple
          accept={ACCEPTED_DOCUMENT_TYPES}
          className="sr-only"
          onChange={onFilesChange}
          aria-invalid={error !== null}
          aria-describedby={error ? 'course-documents-error' : undefined}
        />
        {!hasDocuments && (
          <span className="text-sm text-[var(--color-ink-faint)]">{t('admin.noDocuments')}</span>
        )}
      </div>
      {hasDocuments && (
        <ul className="mt-1 flex flex-col gap-2">
          {savedDocuments.map((savedDocument) => (
            <DocumentRow
              key={savedDocument.id}
              fileName={savedDocument.fileName}
              removeLabel={t('admin.removeDocument')}
              isRemoveDisabled={isRemoveDisabled}
              onRemove={() => onRemoveSaved(savedDocument.id)}
            />
          ))}
          {selectedFiles.map((file) => (
            <DocumentRow
              key={documentFileKey(file)}
              fileName={file.name}
              removeLabel={t('admin.removeDocument')}
              onRemove={() => onRemoveSelected(file)}
            />
          ))}
        </ul>
      )}
      {error && (
        <span id="course-documents-error" role="alert" className="text-sm text-[var(--color-bad)]">
          {error}
        </span>
      )}
    </div>
  );
};
